import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Paintbrush, Download, Save, Loader2, Trash2, Copy, Check, Image as ImageIcon, Palette } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DesignCreator() {
  const { language, t } = useLanguage();
  const [primaryColor, setPrimaryColor] = useState('#0044AA');
  const [secondaryColor, setSecondaryColor] = useState('#333333');
  const [patternId, setPatternId] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [number, setNumber] = useState('');
  const [teamName, setTeamName] = useState('');
  const [patterns, setPatterns] = useState([]);
  const [faceUrl, setFaceUrl] = useState('');
  const [backUrl, setBackUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [myDesigns, setMyDesigns] = useState([]);
  const [showMyDesigns, setShowMyDesigns] = useState(false);
  const debounceRef = useRef(null);
  const [autoPreview, setAutoPreview] = useState(true);

  // Fetch patterns
  useEffect(() => {
    axios.get(`${API}/api/design-v3/patterns`).then(r => setPatterns(r.data)).catch(() => {});
  }, []);

  // Fetch my designs
  const fetchMyDesigns = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/design-v3/my-designs`, { withCredentials: true });
      setMyDesigns(r.data);
    } catch {}
  }, []);

  useEffect(() => { fetchMyDesigns(); }, [fetchMyDesigns]);

  // Generate design
  const generate = useCallback(async () => {
    if (!/^#[0-9a-fA-F]{6}$/.test(primaryColor) || !/^#[0-9a-fA-F]{6}$/.test(secondaryColor)) {
      toast.error('Couleur hexadécimale invalide');
      return;
    }
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/api/design-v3/render`, {
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        pattern_id: patternId,
        player_name: playerName,
        number,
        team_name: teamName,
      }, { withCredentials: true });
      setFaceUrl(`${API}${resp.data.face_url}`);
      setBackUrl(`${API}${resp.data.back_url}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de génération');
    } finally {
      setLoading(false);
    }
  }, [primaryColor, secondaryColor, patternId, playerName, number, teamName]);

  // Auto-preview with debounce
  useEffect(() => {
    if (!autoPreview) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { generate(); }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [primaryColor, secondaryColor, patternId, autoPreview, generate]);

  const handleSave = async () => {
    if (!faceUrl || !backUrl) { toast.error('Générez un design d\'abord'); return; }
    try {
      await axios.post(`${API}/api/design-v3/save`, {
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        pattern_id: patternId,
        player_name: playerName,
        number,
        team_name: teamName,
        face_url: faceUrl,
        back_url: backUrl,
      }, { withCredentials: true });
      toast.success('Design enregistré');
      fetchMyDesigns();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDownload = async (url, filename) => {
    try {
      const resp = await axios.get(url, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(resp.data);
      a.download = filename;
      a.click();
    } catch { toast.error('Erreur téléchargement'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/design-v3/designs/${id}`, { withCredentials: true });
      toast.success('Design supprimé');
      fetchMyDesigns();
    } catch { toast.error('Erreur'); }
  };

  const loadDesign = (d) => {
    setPrimaryColor(d.primary_color);
    setSecondaryColor(d.secondary_color);
    setPatternId(d.pattern_id);
    setPlayerName(d.player_name || '');
    setNumber(d.number || '');
    setTeamName(d.team_name || '');
    setShowMyDesigns(false);
    setAutoPreview(false);
    setTimeout(() => setAutoPreview(true), 600);
  };

  const labels = {
    fr: { title: 'Création de design', primary: 'Couleur primaire', secondary: 'Couleur secondaire', pattern: 'Pattern', name: 'Nom du joueur', num: 'Numéro', team: 'Nom de l\'équipe', generate: 'Générer', save: 'Enregistrer', download: 'Télécharger PNG', face: 'Face', back: 'Dos', myDesigns: 'Mes designs', noDesigns: 'Aucun design enregistré', optional: 'optionnel', preview: 'Aperçu', noPattern: 'Sans motif' },
    en: { title: 'Design Creator', primary: 'Primary color', secondary: 'Secondary color', pattern: 'Pattern', name: 'Player name', num: 'Number', team: 'Team name', generate: 'Generate', save: 'Save', download: 'Download PNG', face: 'Front', back: 'Back', myDesigns: 'My designs', noDesigns: 'No saved designs', optional: 'optional', preview: 'Preview', noPattern: 'No pattern' },
    vi: { title: 'Tạo thiết kế', primary: 'Màu chính', secondary: 'Màu phụ', pattern: 'Họa tiết', name: 'Tên cầu thủ', num: 'Số', team: 'Tên đội', generate: 'Tạo', save: 'Lưu', download: 'Tải PNG', face: 'Mặt trước', back: 'Mặt sau', myDesigns: 'Thiết kế của tôi', noDesigns: 'Chưa có thiết kế', optional: 'tùy chọn', preview: 'Xem trước', noPattern: 'Không có họa tiết' },
  };
  const l = labels[language] || labels.fr;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Paintbrush size={24} className="text-slate-700" />
          <h1 className="text-2xl font-semibold text-slate-900">{l.title}</h1>
        </div>
        <Button variant="outline" className="gap-1.5" onClick={() => { setShowMyDesigns(true); fetchMyDesigns(); }}>
          <ImageIcon size={16} /> {l.myDesigns} {myDesigns.length > 0 && <Badge className="ml-1 bg-slate-200 text-slate-700">{myDesigns.length}</Badge>}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left panel — Configuration */}
        <Card className="border-slate-200 lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Palette size={18} /> Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Primary color */}
            <div>
              <Label className="text-xs font-semibold mb-1.5 block">{l.primary}</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)}
                  className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5" />
                <Input value={primaryColor} onChange={e => { const v = e.target.value; if (/^#?[0-9a-fA-F]{0,6}$/.test(v)) setPrimaryColor(v.startsWith('#') ? v : `#${v}`); }}
                  className="h-10 font-mono text-sm flex-1" maxLength={7} />
              </div>
            </div>

            {/* Secondary color */}
            <div>
              <Label className="text-xs font-semibold mb-1.5 block">{l.secondary}</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)}
                  className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5" />
                <Input value={secondaryColor} onChange={e => { const v = e.target.value; if (/^#?[0-9a-fA-F]{0,6}$/.test(v)) setSecondaryColor(v.startsWith('#') ? v : `#${v}`); }}
                  className="h-10 font-mono text-sm flex-1" maxLength={7} />
              </div>
            </div>

            {/* Pattern gallery */}
            <div>
              <Label className="text-xs font-semibold mb-1.5 block">{l.pattern}</Label>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => setPatternId('')}
                  className={`p-2 rounded-lg border-2 text-center text-[10px] font-medium transition-all ${!patternId ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                  {l.noPattern}
                </button>
                {patterns.map(p => (
                  <button key={p.id} onClick={() => setPatternId(p.local_id || p.id)}
                    className={`p-2 rounded-lg border-2 text-center transition-all ${(p.local_id || p.id) === patternId ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    {p.url ? (
                      <img src={p.url} alt={p.name} className="w-full h-12 object-contain rounded mb-1 bg-slate-100" />
                    ) : (
                      <div className="w-full h-12 bg-slate-100 rounded mb-1 flex items-center justify-center text-slate-400"><ImageIcon size={16} /></div>
                    )}
                    <span className="text-[10px] font-medium text-slate-700">{p.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Optional fields */}
            <div className="space-y-3 pt-2 border-t border-slate-100">
              <div>
                <Label className="text-xs text-slate-500">{l.name} <span className="text-slate-400">({l.optional})</span></Label>
                <Input value={playerName} onChange={e => setPlayerName(e.target.value)} placeholder="DUPONT" className="h-9 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">{l.num} <span className="text-slate-400">({l.optional})</span></Label>
                <Input value={number} onChange={e => setNumber(e.target.value)} placeholder="10" className="h-9 mt-1" maxLength={3} />
              </div>
              <div>
                <Label className="text-xs text-slate-500">{l.team} <span className="text-slate-400">({l.optional})</span></Label>
                <Input value={teamName} onChange={e => setTeamName(e.target.value)} placeholder="FRANCE" className="h-9 mt-1" />
              </div>
            </div>

            {/* Generate button */}
            <Button data-testid="design-generate-btn" className="w-full gap-2 bg-slate-900 hover:bg-slate-800" onClick={generate} disabled={loading}>
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Paintbrush size={16} />}
              {l.generate}
            </Button>
          </CardContent>
        </Card>

        {/* Right panel — Preview */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{l.preview}</CardTitle>
              {(faceUrl || backUrl) && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => handleDownload(faceUrl, 'jersey_face.png')}>
                    <Download size={14} /> {l.face}
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => handleDownload(backUrl, 'jersey_back.png')}>
                    <Download size={14} /> {l.back}
                  </Button>
                  <Button size="sm" className="gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={handleSave}>
                    <Save size={14} /> {l.save}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 size={32} className="animate-spin text-slate-400" />
              </div>
            ) : (faceUrl && backUrl) ? (
              <div className="flex gap-6 justify-center items-start bg-gradient-to-b from-slate-100 to-slate-50 rounded-xl p-6">
                <div className="text-center">
                  <img src={faceUrl} alt="Face" className="max-h-[500px] mx-auto drop-shadow-lg" />
                  <p className="text-xs font-semibold text-slate-500 mt-2">{l.face}</p>
                </div>
                <div className="text-center">
                  <img src={backUrl} alt="Back" className="max-h-[500px] mx-auto drop-shadow-lg" />
                  <p className="text-xs font-semibold text-slate-500 mt-2">{l.back}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-20 text-slate-400">
                <div className="text-center">
                  <Paintbrush size={40} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">{language === 'vi' ? 'Chọn màu và nhấn Tạo' : language === 'en' ? 'Choose colors and press Generate' : 'Choisissez les couleurs et cliquez Générer'}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* My Designs Dialog */}
      <Dialog open={showMyDesigns} onOpenChange={setShowMyDesigns}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{l.myDesigns}</DialogTitle></DialogHeader>
          {myDesigns.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">{l.noDesigns}</p>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {myDesigns.map(d => (
                <div key={d.id} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 transition-colors">
                  <div className="flex gap-2 mb-2">
                    {d.face_url && <img src={d.face_url} alt="" className="h-24 object-contain bg-slate-100 rounded" />}
                    {d.back_url && <img src={d.back_url} alt="" className="h-24 object-contain bg-slate-100 rounded" />}
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 rounded-full border" style={{ backgroundColor: d.primary_color }} />
                    <div className="w-5 h-5 rounded-full border" style={{ backgroundColor: d.secondary_color }} />
                    <span className="text-xs text-slate-500">{d.player_name || ''} {d.number || ''} {d.team_name || ''}</span>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="text-xs gap-1 flex-1" onClick={() => loadDesign(d)}>
                      <Copy size={12} /> Ouvrir
                    </Button>
                    <Button size="sm" variant="ghost" className="text-xs text-red-500 gap-1" onClick={() => handleDelete(d.id)}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1">{d.created_at?.slice(0, 10)}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
